import { PDFDocument } from "pdf-lib";
import type { CurvaDocument } from "@/document/model/document";
import type { FrameNode } from "@/document/model/nodes";
import { createPngExportSession } from "./pngExporter";

const defaultPdfRasterScale = 2;

export type PdfExportProgress =
  | {
    phase: "rendering";
    current: number;
    total: number;
    frameName: string;
  }
  | {
    phase: "finalizing";
    total: number;
  };

export interface PdfExportOptions {
  scale?: number;
  onProgress?: (progress: PdfExportProgress) => void;
}

/** Yields to the event loop so the browser can paint progress between frames. */
function yieldToEventLoop(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

export async function exportFramesToPdfBlob(
  document: CurvaDocument,
  frames: FrameNode[],
  options: PdfExportOptions = {},
): Promise<Blob> {
  if (frames.length === 0) {
    throw new Error("No frames to export.");
  }

  const scale = options.scale ?? defaultPdfRasterScale;
  const pdfDoc = await PDFDocument.create();
  // Reuse a single WebGL context for the whole batch. Creating one renderer per
  // frame exhausts the browser's live-context limit and freezes the tab.
  const session = createPngExportSession();

  try {
    for (let index = 0; index < frames.length; index += 1) {
      const frame = frames[index]!;
      options.onProgress?.({
        phase: "rendering",
        current: index + 1,
        total: frames.length,
        frameName: frame.name,
      });

      const pngBlob = await session.renderNodeToPngBlob(document, frame, scale);
      const pngBytes = new Uint8Array(await pngBlob.arrayBuffer());
      const image = await pdfDoc.embedPng(pngBytes);
      const width = image.width / scale;
      const height = image.height / scale;
      const page = pdfDoc.addPage([width, height]);
      page.drawImage(image, {
        x: 0,
        y: 0,
        width,
        height,
      });

      // Let the browser repaint and stay responsive during large batches.
      await yieldToEventLoop();
    }
  } finally {
    session.dispose();
  }

  options.onProgress?.({ phase: "finalizing", total: frames.length });
  // Ensure the final frame message is replaced before pdf-lib begins serializing.
  await yieldToEventLoop();
  // pdf-lib can spend a long time serializing large raster PDFs. A low
  // objectsPerTick keeps its writer cooperative so browser paint and timers
  // continue to run during this otherwise opaque phase.
  const pdfBytes = await pdfDoc.save({ objectsPerTick: 5 });
  const pdfBuffer = pdfBytes.buffer.slice(pdfBytes.byteOffset, pdfBytes.byteOffset + pdfBytes.byteLength) as ArrayBuffer;
  return new Blob([pdfBuffer], { type: "application/pdf" });
}
